/**
 * /tracking — turning a day of visit rows into positions, routes and roster state.
 *
 * Pure and dependency-free on purpose: `scripts/check-tracking.mjs` imports it directly under
 * node, the same way `check-csv.mjs` imports `import/csv.js`. No React, no dayjs, no formatting —
 * the view owns every string.
 *
 * The whole page rests on one derivation, so it is worth stating plainly: THERE IS NO LIVE GPS IN
 * THIS SYSTEM. A merchandiser's position is the coordinate of the POINT of their currently open
 * visit; their last known position is the point of their last closed one. `Visit.meta` — the real
 * 30-minute-cadence trail the mobile app records — is out of scope: it only exists on
 * `GET visits/{id}/` and only lands at visit close.
 */

/* An open visit older than this is a forgotten close, not somebody standing in a shop. It is a
   WARNING, not a fault — the merchandiser is probably working and needs a nudge to close it. */
export const STALE_OPEN_H = 4;

export const STATES = {
  IN_VISIT: "in_visit",
  OPEN_STALE: "open_stale",
  BETWEEN: "between",
  IDLE: "idle",
};

/* Tones match `StatusIcon`'s in list-page.scss, so a pin and its rail row always agree.
   `off` is neutral rather than negative: between points is a resting state, not a problem. */
export const TONE = {
  [STATES.IN_VISIT]: "ok",
  [STATES.OPEN_STALE]: "wait",
  [STATES.BETWEEN]: "off",
  [STATES.IDLE]: "off",
};

/* Sort order for the rail: the rows that want a phone call first, then who is out there, then who
   has finished, then who has not started. */
const RANK = {
  [STATES.OPEN_STALE]: 0,
  [STATES.IN_VISIT]: 1,
  [STATES.BETWEEN]: 2,
  [STATES.IDLE]: 3,
};

/* ⚠ `Number(null)` is 0 and `Number("")` is 0, so a missing coordinate would read as the
   equator/prime-meridian rather than as absent. Reject the empty cases before converting.
   Values arrive as floats from the read serializer and as strings from the write one. */
const num = value => {
  if (value === null || value === undefined || value === "") return NaN;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const ms = value => {
  const parsed = value ? Date.parse(value) : NaN;
  return Number.isFinite(parsed) ? parsed : NaN;
};

/* `Point.location` is nullable, and /points ships a "no coordinates" filter precisely because
   such points exist. A visit at one keeps its rail row and loses only its pin. */
export const coordsOf = point => {
  const location = point && point.location;
  if (!location) return null;
  const lat = num(location.latitude);
  const lng = num(location.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
};

/* There is no `end__isnull` on the endpoint, so open-vs-closed is decided here. */
export const isOpen = visit => !visit.end;

export const startedAt = visit => ms(visit && visit.start);
export const endedAt = visit => ms(visit && visit.end);

/* ⚠ NEVER read `time_spent`: it is a timedelta string on a closed visit and the integer 0 on an
   open one. One subtraction is one code path and no parser. */
export const durationMs = (visit, now) => {
  const from = startedAt(visit);
  if (!Number.isFinite(from)) return null;
  const to = isOpen(visit) ? now : endedAt(visit);
  if (!Number.isFinite(to)) return null;
  return Math.max(0, to - from);
};

/* The day in visit order. `?ordering=start` already asks the server for this, but the rows arrive
   across several pages, so sorting here is what makes the sequence numbers trustworthy. */
export const byStart = visits => (visits || []).filter(visit => Number.isFinite(startedAt(visit))).sort((a, b) => startedAt(a) - startedAt(b));

export const groupByUser = visits => {
  const out = new Map();
  (visits || []).forEach(visit => {
    const id = visit && visit.user && visit.user.id;
    if (id === null || id === undefined) return;
    if (!out.has(id)) out.set(id, []);
    out.get(id).push(visit);
  });
  return out;
};

/**
 * One merchandiser's day → where they are, and the ordered sequence they walked.
 * `since` is when the current state began: the open visit's start, or the last visit's end.
 */
export const stateFor = (visits, now) => {
  const sequence = byStart(visits);
  if (!sequence.length) return { kind: STATES.IDLE, visit: null, since: null, sequence };

  /* ⚠ The LAST open row, not the first. Two open visits happen — a merchandiser who forgot to
     close one and started the next — and the newer one is where they actually are. */
  let open = null;
  for (let i = sequence.length - 1; i >= 0; i -= 1) {
    if (isOpen(sequence[i])) {
      open = sequence[i];
      break;
    }
  }

  if (open) {
    const age = now - startedAt(open);
    return {
      kind: age >= STALE_OPEN_H * 3600000 ? STATES.OPEN_STALE : STATES.IN_VISIT,
      visit: open,
      since: startedAt(open),
      sequence,
    };
  }

  const last = sequence[sequence.length - 1];
  return { kind: STATES.BETWEEN, visit: last, since: endedAt(last), sequence };
};

/**
 * The roster: one row per active merchandiser, plus anyone who has visits today but is missing
 * from the merchandiser list — deactivated mid-day, or beyond the roster walk's page bound.
 * Dropping a person who is demonstrably working would be the worst kind of silent omission.
 */
export const rosterRows = (merchandisers, visits, now) => {
  const byUser = groupByUser(visits);
  const seen = new Set();
  const rows = [];

  (merchandisers || []).forEach(user => {
    if (user === null || user === undefined || user.id === null || user.id === undefined) return;
    if (seen.has(user.id)) return;
    seen.add(user.id);
    rows.push({ user, ...stateFor(byUser.get(user.id) || [], now) });
  });

  byUser.forEach((userVisits, id) => {
    if (seen.has(id)) return;
    seen.add(id);
    rows.push({ user: userVisits[0].user, extra: true, ...stateFor(userVisits, now) });
  });

  const name = row => `${row.user.first_name || ""} ${row.user.last_name || ""}`.trim().toLowerCase();
  return rows.sort((a, b) => RANK[a.kind] - RANK[b.kind] || name(a).localeCompare(name(b)));
};

/* The resting map: everyone with a position — an open visit (in_visit / open_stale) OR the point
   of their last closed one (between). The TONE per kind is what keeps a last-known pin from
   reading as a live position: grey says "was here", green/amber say "is here". Idle rows have no
   visit and stay rail-only. Positions are shop-granular either way — there is no live GPS. */
export const mapRows = rows => (rows || []).filter(row => row.kind !== STATES.IDLE);

/**
 * One merchandiser's visited points as numbered pins, the open visit marked as "here now".
 * Numbering counts every visit in the day, so a coordinate-less point leaves a gap rather than
 * silently renumbering the ones around it.
 */
export const sequenceMarkers = sequence =>
  (sequence || [])
    .map((visit, index) => {
      const at = coordsOf(visit.point);
      if (!at) return null;
      const here = isOpen(visit);
      return {
        key: `v${visit.id}`,
        lat: at[0],
        lng: at[1],
        label: index + 1,
        kind: here ? "here" : null,
        tone: here ? "ok" : "off",
        title: (visit.point && visit.point.name) || "",
        visit,
      };
    })
    .filter(Boolean);

/* The path actually walked — shop to shop, which is as fine-grained as this data gets. */
export const trackOf = sequence => (sequence || []).map(visit => coordsOf(visit.point)).filter(Boolean);

/* Planned stops that were not visited. Compared by point id, so revisiting a point still counts
   it as done and a plan is never shown as outstanding when it was met. */
export const planPending = (stops, sequence) => {
  const visited = new Set((sequence || []).map(visit => visit.point && visit.point.id).filter(id => id !== null && id !== undefined));
  return (stops || []).filter(stop => stop && stop.point && !visited.has(stop.point.id));
};

export const planMarkers = (stops, sequence) =>
  planPending(stops, sequence)
    .map(stop => {
      const at = coordsOf(stop.point);
      if (!at) return null;
      return {
        key: `p${stop.point.id}`,
        lat: at[0],
        lng: at[1],
        label: "",
        kind: "plan",
        title: stop.point.name || "",
        point: stop.point,
      };
    })
    .filter(Boolean);

/* The whole intended route, visited or not — the dashed line the solid one is compared against. */
export const planTrackOf = stops => (stops || []).map(stop => coordsOf(stop && stop.point)).filter(Boolean);
